import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CalendarDays, Menu, X, User, LogOut, Sun, Moon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { api } from '../lib/api';
import { canSeeCommunity } from '../lib/community';

const isExternalUrl = (url) => /^(https?:|mailto:|tel:|wa\.me)/i.test(url || '');

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const [banner, setBanner] = useState(null);
  const [bannerHeight, setBannerHeight] = useState(34);

  const bannerRef = useRef(null);
  const navRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/banner')
      .then((data) => { if (!cancelled) setBanner(data); })
      .catch(() => { if (!cancelled) setBanner(null); });
    return () => { cancelled = true; };
  }, []);

  const updateScroll = useCallback(() => {
    const s = window.scrollY;
    const total = document.body.scrollHeight - window.innerHeight;
    const pct = total > 0 ? (s / total) * 100 : 0;

    if (progressRef.current) {
      progressRef.current.style.width = `${pct}%`;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', updateScroll, { passive: true });
    return () => window.removeEventListener('scroll', updateScroll);
  }, [updateScroll]);

  const active = (p) => location.pathname === p;
  const links = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/courses', label: 'Courses' },
    { to: '/blog', label: 'Blog' },
    // The /answers hub was reachable only from the footer, and the footer is not in
    // the crawlable shell — so nothing a non-JS fetcher reads pointed at it. Google
    // Search Console reports 25 of the answer pages as "URL is unknown to Google"
    // despite being in the sitemap since June; a sitemap alone was not enough.
    { to: '/answers', label: 'Answers' },
    { to: '/reviews', label: 'Reviews' },
    { to: '/contact', label: 'Contact' },
    ...(canSeeCommunity(user) ? [{ to: '/community', label: 'Community' }] : []),
  ];

  const handleLogout = async () => {
    await logout();
    setMobileOpen(false);
  };

  const bannerItems = (banner?.items || []).filter((it) => it && it.message);
  const showBanner = banner && banner.enabled && bannerItems.length > 0;

  // Stacked banner: every announcement is visible. Its height is variable, so measure
  // it and push the nav down by exactly that much.
  useLayoutEffect(() => {
    if (!showBanner || !bannerRef.current) return;
    const el = bannerRef.current;
    const measure = () => setBannerHeight(el.offsetHeight);
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [showBanner, bannerItems.length, banner?.heading]);

  // Publish the banner height as a CSS var so page content (e.g. the hero) can pad
  // below the full fixed header (banner + nav) and never sit under it.
  useEffect(() => {
    document.documentElement.style.setProperty('--banner-h', showBanner ? `${bannerHeight}px` : '0px');
  }, [showBanner, bannerHeight]);

  const bannerRowInner = (it) => (
    <span className="inline-flex items-center gap-1.5 sm:gap-2 min-w-0">
      <CalendarDays size={13} className="flex-shrink-0" style={{ color: 'var(--accent)' }} />
      <span className="font-['Lato'] text-[11px] sm:text-[12px] tracking-[0.06em] sm:tracking-[0.1em] truncate">
        {it.message}
      </span>
      {it.cta_text && (
        <span className="font-['Lato'] text-[10px] sm:text-[11px] font-bold tracking-[0.14em] whitespace-nowrap flex-shrink-0" style={{ color: 'var(--accent)' }}>
          {it.cta_text} →
        </span>
      )}
    </span>
  );

  const bannerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1001,
    background: 'var(--bg-primary)',
    borderBottom: '1px solid var(--border-light)',
    boxShadow: '0 1px 0 var(--border-subtle)',
    color: 'var(--accent-text-strong)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '6px 16px',
    pointerEvents: 'auto',
  };
  const bannerLinkStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    color: 'var(--accent-text-strong)',
    textDecoration: 'none',
  };

  return (
    <>
      {showBanner && (
        <div ref={bannerRef} style={bannerStyle}>
          {banner?.heading && (
            <div className="text-center font-['Lato'] text-[10px] font-bold tracking-[0.22em]" style={{ color: 'var(--accent-text-muted)', marginBottom: '3px' }}>
              {banner.heading}
            </div>
          )}
          {/* All announcements inline on one row (with dividers); wraps on small screens. */}
          <div className="flex flex-wrap items-center justify-center gap-x-3 sm:gap-x-5 gap-y-1" style={{ maxWidth: '1360px', width: '100%' }}>
            {bannerItems.map((it, i) => {
              const href = it.cta_url || '#';
              return (
                <React.Fragment key={i}>
                  {i > 0 && (
                    <span aria-hidden="true" className="hidden sm:inline-block flex-shrink-0" style={{ width: '1px', height: '13px', background: 'var(--border-medium)' }} />
                  )}
                  {isExternalUrl(href) ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" data-cursor-hover style={bannerLinkStyle}>
                      {bannerRowInner(it)}
                    </a>
                  ) : (
                    <Link to={href} data-cursor-hover style={bannerLinkStyle}>
                      {bannerRowInner(it)}
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      <nav ref={navRef} style={{
        position: 'fixed', top: showBanner ? `${bannerHeight}px` : '0', left: 0, right: 0, zIndex: 1000,
        height: '80px',
        background: 'var(--bg-primary)',
        borderBottom: '1px solid var(--border-light)',
        boxShadow: '0 6px 18px var(--shadow-color)',
        transition: 'border-color 0.4s ease, box-shadow 0.4s ease',
        pointerEvents: 'none',
      }}>
        {/* Scroll progress */}
        <div ref={progressRef} style={{ position: 'absolute', bottom: 0, left: 0, height: '1px', width: '0%', background: 'linear-gradient(90deg, var(--accent-deep), var(--accent))', transition: 'width 0.1s linear' }} />

        <div style={{ maxWidth: '1360px', margin: '0 auto', padding: '0 48px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pointerEvents: 'auto' }}>

          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0', marginTop: '4px' }}>
            <div>
              <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: '23px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '0.2em', lineHeight: 1.05 }}>
                BREWING<br /><span style={{ color: 'var(--accent)' }}>SKILL CO.</span>
              </div>
              <div style={{ fontFamily: 'Dancing Script, cursive', fontSize: '13px', color: 'var(--accent-deep)', letterSpacing: '0.12em', marginTop: '3px' }}>
                — CAFÉ ACADEMY —
              </div>
            </div>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex" style={{ alignItems: 'center', gap: '44px' }}>
            {links.map(l => (
              <Link key={l.to} to={l.to} style={{
                fontFamily: 'Lato, sans-serif', fontSize: '14px',
                fontWeight: active(l.to) ? 700 : 500,
                color: active(l.to) ? 'var(--accent)' : 'var(--text-secondary)',
                textDecoration: 'none', letterSpacing: '0.1em',
                position: 'relative', padding: '4px 0',
                transition: 'color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                onMouseLeave={e => e.currentTarget.style.color = active(l.to) ? 'var(--accent)' : 'var(--text-secondary)'}
                data-cursor-hover
              >
                {l.label}
                <span style={{ position: 'absolute', bottom: 0, left: 0, height: '1px', width: active(l.to) ? '100%' : '0%', background: 'var(--accent-deep)', transition: 'width 0.3s ease', display: 'block' }} />
              </Link>
            ))}

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              data-cursor-hover
              aria-label="Toggle theme"
              style={{
                background: 'none', border: '1px solid var(--border-strong)',
                borderRadius: '8px', padding: '6px',
                color: 'var(--accent-text-strong)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'color 0.2s, border-color 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.borderColor = 'var(--border-accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--accent-text-strong)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            {isAuthenticated ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Link
                  to={user?.role === 'admin' ? '/admin' : '/dashboard'}
                  data-cursor-hover
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    fontFamily: 'Lato, sans-serif', fontSize: '14px',
                    color: 'var(--text-muted)',
                    textDecoration: 'none',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                >
                  {user?.profile_photo ? (
                    <img src={user.profile_photo} alt="" style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--border-accent)' }} />
                  ) : (
                    <div style={{ width: '30px', height: '30px', borderRadius: '50%', backgroundColor: 'var(--accent-bg)', border: '1px solid var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <User size={15} style={{ color: 'var(--accent-deep)' }} />
                    </div>
                  )}
                  <span>{user?.name?.split(' ')[0] || 'Dashboard'}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  data-cursor-hover
                  data-testid="navbar-logout-btn"
                  style={{
                    background: 'none', border: 'none', cursor: 'none',
                    color: 'var(--accent-text-muted)', padding: '4px',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--accent-text-muted)'}
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <Link to="/login" data-cursor-hover style={{
                fontFamily: 'Lato, sans-serif', fontSize: '12px', fontWeight: 700, letterSpacing: '0.18em',
                color: 'var(--text-on-accent)', backgroundColor: 'var(--accent)',
                padding: '10px 24px', borderRadius: '8px', textDecoration: 'none',
                transition: 'background-color 0.25s',
              }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--accent-deep)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--accent)'}
              >
                LOGIN
              </Link>
            )}
          </div>

          {/* Mobile toggle + theme */}
          <div className="flex md:hidden" style={{ alignItems: 'center', gap: '8px' }}>
            <button onClick={toggleTheme} aria-label="Toggle theme"
              style={{ background: 'none', border: 'none', color: 'var(--accent-text-medium)', cursor: 'pointer', padding: '8px' }}>
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button onClick={() => setMobileOpen(!mobileOpen)}
              style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'none', padding: '8px' }}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div style={{ maxHeight: mobileOpen ? '420px' : '0', overflow: 'hidden', transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)', backgroundColor: 'var(--bg-tertiary)', borderTop: mobileOpen ? '1px solid var(--border-light)' : 'none', pointerEvents: 'auto' }}>
          <div style={{ padding: '28px 48px 40px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {links.map((l, i) => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} style={{
                fontFamily: 'Oswald, sans-serif', fontSize: '24px', color: active(l.to) ? 'var(--accent)' : 'var(--text-secondary)', textDecoration: 'none', letterSpacing: '0.1em',
                opacity: mobileOpen ? 1 : 0, transform: mobileOpen ? 'translateX(0)' : 'translateX(-20px)',
                transition: `opacity 0.3s ease ${i * 0.06}s, transform 0.3s ease ${i * 0.06}s`,
              }}>
                {l.label.toUpperCase()}
              </Link>
            ))}

            {isAuthenticated ? (
              <>
                <Link
                  to={user?.role === 'admin' ? '/admin' : '/dashboard'}
                  onClick={() => setMobileOpen(false)}
                  style={{
                    fontFamily: 'Oswald, sans-serif', fontSize: '24px', color: 'var(--accent)', textDecoration: 'none', letterSpacing: '0.1em',
                    opacity: mobileOpen ? 1 : 0, transform: mobileOpen ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `opacity 0.3s ease ${links.length * 0.06}s, transform 0.3s ease ${links.length * 0.06}s`,
                  }}
                >
                  {user?.role === 'admin' ? 'ADMIN PANEL' : 'MY DASHBOARD'}
                </Link>
                <button
                  onClick={handleLogout}
                  style={{
                    fontFamily: 'Oswald, sans-serif', fontSize: '24px', color: 'var(--text-faded)', textDecoration: 'none', letterSpacing: '0.1em', background: 'none', border: 'none', textAlign: 'left', cursor: 'none',
                    opacity: mobileOpen ? 1 : 0, transform: mobileOpen ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `opacity 0.3s ease ${(links.length + 1) * 0.06}s, transform 0.3s ease ${(links.length + 1) * 0.06}s`,
                  }}
                >
                  LOGOUT
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMobileOpen(false)} style={{
                fontFamily: 'Oswald, sans-serif', fontSize: '24px', color: 'var(--accent)', textDecoration: 'none', letterSpacing: '0.1em',
                opacity: mobileOpen ? 1 : 0, transform: mobileOpen ? 'translateX(0)' : 'translateX(-20px)',
                transition: `opacity 0.3s ease ${links.length * 0.06}s, transform 0.3s ease ${links.length * 0.06}s`,
              }}>
                LOGIN
              </Link>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
